import type { FlowCardDefinition } from '../types';

interface NotificationVariant {
	paletteKey?: string;
	label: string;
	description: string;
	icon: string;
	color?: string;
	notificationType: string;
	pushMsgType?: 'push' | 'alarm';
}

const variants: NotificationVariant[] = [
	{
		label: 'Notification',
		description: 'Send a push notification or alert',
		icon: 'fa-bell',
		notificationType: 'push_notification',
	},
	{
		paletteKey: 'emailNotification',
		label: 'Email Notification',
		description: 'Send an email when the branch runs',
		icon: 'fa-envelope',
		notificationType: 'mail_notification',
	},
	{
		paletteKey: 'eventLogNotification',
		label: 'Event Log',
		description: 'Record an entry in the automation event log',
		icon: 'fa-list',
		notificationType: 'event_log',
	},
	{
		paletteKey: 'alexaNotification',
		label: 'Alexa Notification',
		description: 'Announce via Alexa (requires linked account)',
		icon: 'fa-volume-high',
		notificationType: 'alexa_notif',
	},
	{
		paletteKey: 'pushAlarmNotification',
		label: 'Push Alarm',
		description: 'Send a high-priority alarm push notification',
		icon: 'fa-bell-exclamation',
		color: '#ef4444',
		notificationType: 'push_notification',
		pushMsgType: 'alarm',
	},
];

function notificationVariantForData(data: Record<string, any>): NotificationVariant {
	const type = String(data.notificationType || 'push_notification');
	if (type === 'mail_notification') return variants[1];
	if (type === 'event_log') return variants[2];
	if (type.includes('alexa')) return variants[3];
	if (String(data.pushMsgType || 'push') === 'alarm') return variants[4];
	return variants[0];
}

export function normalizeNotificationCardData(data: Record<string, any>): Record<string, any> {
	const selected = notificationVariantForData(data);
	return {
		...data,
		paletteKey: selected.paletteKey || 'notificationAction',
		label: selected.label,
		icon: selected.icon,
		color: selected.color || '#f97316',
	};
}

function notificationCard(variant: NotificationVariant): FlowCardDefinition {
	return {
		type: 'notificationAction',
		...(variant.paletteKey ? { paletteKey: variant.paletteKey } : {}),
		version: '1.0.0',
		domain: 'core',
		role: 'action',
		category: 'actions',
		label: variant.label,
		description: variant.description,
		icon: variant.icon,
		color: variant.color || '#f97316',
		schema: {
			$schema: 'https://json-schema.org/draft/2020-12/schema',
			type: 'object',
			required: ['notificationType'],
			properties: {
				notificationType: {
					type: 'string',
					enum: [
						'push_notification',
						'mail_notification',
						'event_log',
						'alexa_notif',
						'alexa_notif_premium',
					],
				},
				notificationMessage: { type: 'string' },
				pushMsgType: { type: 'string', enum: ['push', 'alarm'] },
				delay: {
					type: 'object',
					properties: {
						value: { type: 'number', minimum: 0 },
						units: { type: 'string', enum: ['sec', 'min'] },
					},
				},
			},
		},
		fields: [
			{
				path: 'notificationType',
				label: 'Notification type',
				type: 'select',
				options: [
					{ value: 'push_notification', label: 'Push notification' },
					{ value: 'mail_notification', label: 'Email notification' },
					{ value: 'event_log', label: 'Event log' },
					{ value: 'alexa_notif', label: 'Alexa notification' },
					{ value: 'alexa_notif_premium', label: 'Alexa announcement (Premium)' },
				],
			},
			{
				path: 'pushMsgType',
				label: 'Message priority',
				type: 'select',
				visibleWhen: { path: 'notificationType', equals: 'push_notification' },
				options: [
					{ value: 'push', label: 'Standard' },
					{ value: 'alarm', label: 'Alarm' },
				],
			},
			{
				path: 'notificationMessage',
				label: variant.paletteKey === 'alexaNotification' ? 'Announcement' : 'Message',
				type: 'textarea',
				placeholder:
					variant.paletteKey === 'alexaNotification'
						? 'Enter what Alexa should announce…'
						: 'Enter the message…',
				visibleWhen: {
					path: 'notificationType',
					oneOf: [
						'push_notification',
						'mail_notification',
						'alexa_notif',
						'alexa_notif_premium',
					],
				},
			},
			{ path: 'delay.value', label: 'Delay', type: 'number', min: 0, group: 'Timing' },
			{
				path: 'delay.units',
				label: 'Delay unit',
				type: 'select',
				group: 'Timing',
				options: [
					{ value: 'sec', label: 'Seconds' },
					{ value: 'min', label: 'Minutes' },
				],
			},
		],
		ports: { sources: ['output'] },
		defaults: () => ({
			notificationType: variant.notificationType,
			notificationMessage: '',
			pushMsgType: variant.pushMsgType || 'push',
			delay: { value: 0, units: 'sec' },
			cardVersion: '1.0.0',
		}),
		summary: (data) => {
			const message = String(data.notificationMessage || '').trim();
			return message || notificationVariantForData(data).label;
		},
		normalize: normalizeNotificationCardData,
		validate: (data) => {
			const needsMessage = [
				'push_notification',
				'mail_notification',
				'alexa_notif',
				'alexa_notif_premium',
			].includes(String(data.notificationType || ''));
			const message = data.notificationMessage ?? data.sceneData?.msg ?? '';
			return needsMessage && !String(message).trim()
				? [
						{
							path: 'notificationMessage',
							message: String(data.notificationType || '').includes('alexa')
								? 'Alexa announcement required'
								: 'Notification message required',
						},
					]
				: [];
		},
		compile: (node) => ({
			notify: String(node.data.notificationType || variant.notificationType),
			msg: String(node.data.notificationMessage || ''),
			msg_type: String(node.data.pushMsgType || variant.pushMsgType || 'push'),
			_gui_type: 'notification',
			_gui_purpose: 'action',
			_flow_card: String(node.data.paletteKey || variant.paletteKey || 'notificationAction'),
			_flow_card_version: '1.0.0',
		}),
		migrate: (data) =>
			normalizeNotificationCardData({
				...data,
				notificationType:
					data.notificationType ?? (data.sceneData as any)?.notify ?? variant.notificationType,
				notificationMessage:
					data.notificationMessage ?? (data.sceneData as any)?.msg ?? data.msg ?? '',
				pushMsgType:
					data.pushMsgType ??
					(data.sceneData as any)?.msg_type ??
					variant.pushMsgType ??
					'push',
				delay: data.delay ?? { value: 0, units: 'sec' },
			}),
	};
}

export const notificationCards = variants.map(notificationCard);
